<template>
  <div class="rollout-form">
    <div class="rollout-form__tabs-shell">
      <div class="rollout-form__tab-panel">
        <div class="rollout-form__tab-panel-body">
          <v-alert v-if="error" type="error" dense class="mb-3 mx-4 mt-4" data-testid="rollout-form-error">
            {{ error }}
          </v-alert>

          <div v-if="loading" class="d-flex justify-center pa-8">
            <v-progress-circular indeterminate color="primary" />
          </div>

          <v-form v-else class="mt-2" ref="form" v-model="valid">
            <v-card class="mb-4 rollout-form__section" elevation="1">
              <v-card-title class="d-flex align-center rollout-form__section-title">
                <v-icon class="mr-2">mdi-rocket-launch</v-icon>
                <span class="font-weight-bold">Progressive Rollout</span>
              </v-card-title>
              <v-card-text>
                <v-row dense v-if="isNew">
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="form.feedId"
                      :items="feeds"
                      item-text="name"
                      item-value="id"
                      label="Feed *"
                      :rules="[rules.required]"
                      :disabled="saving"
                      dense
                      hide-details="auto"
                      data-testid="rollout-form-feed"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      v-model="form.gameId"
                      :items="games"
                      :item-text="gameLabel"
                      item-value="id"
                      label="Game *"
                      :rules="[rules.required]"
                      :disabled="saving"
                      clearable
                      dense
                      hide-details="auto"
                      data-testid="rollout-form-game"
                    />
                  </v-col>
                </v-row>

                <v-row dense v-else>
                  <v-col cols="12" md="6">
                    <v-text-field
                      :value="formatFeedLabel()"
                      label="Feed"
                      readonly
                      disabled
                      dense
                      hide-details="auto"
                      data-testid="rollout-form-feed-readonly"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      :value="formatGameLabel()"
                      label="Game"
                      readonly
                      disabled
                      dense
                      hide-details="auto"
                      data-testid="rollout-form-game-readonly"
                    />
                  </v-col>
                </v-row>

                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="form.status"
                      :items="statusItems"
                      label="Status"
                      :disabled="saving"
                      dense
                      hide-details="auto"
                      data-testid="rollout-form-status"
                      @change="onStatusChange"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="form.segmentation"
                      :items="segmentationItems"
                      label="Segmentation"
                      :disabled="saving"
                      dense
                      hide-details="auto"
                      data-testid="rollout-form-segmentation"
                    />
                  </v-col>
                </v-row>

                <v-alert
                  v-if="form.status"
                  type="info"
                  dense
                  outlined
                  class="mb-2"
                  data-testid="rollout-status-description"
                >
                  {{ statusDescription }}
                </v-alert>

                <v-row dense>
                  <v-col cols="12" md="8" class="rollout-form__slider-col">
                    <v-slider
                      v-model="form.percentage"
                      :min="percentageMin"
                      :max="percentageMax"
                      :step="5"
                      thumb-label="always"
                      :disabled="percentageDisabled || saving"
                      hide-details
                      data-testid="rollout-form-percentage-slider"
                      @change="onPercentageSliderChange"
                    >
                      <template v-slot:prepend>
                        <span class="text-caption">Percentage</span>
                      </template>
                    </v-slider>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model.number="form.percentage"
                      type="number"
                      label="Percentage"
                      :min="percentageMin"
                      :max="percentageMax"
                      :disabled="percentageDisabled || saving"
                      suffix="%"
                      dense
                      hide-details="auto"
                      data-testid="rollout-form-percentage-input"
                      @change="onPercentageInputChange"
                    />
                  </v-col>
                </v-row>

                <v-row dense>
                  <v-col cols="12">
                    <v-textarea
                      v-model="form.comment"
                      label="Comment"
                      rows="2"
                      auto-grow
                      :disabled="saving"
                      dense
                      hide-details="auto"
                      data-testid="rollout-form-comment"
                    />
                  </v-col>
                </v-row>

                <div v-if="!isNew" class="mt-2 grey--text text--darken-1 caption">
                  <div v-if="item.updatedAt">
                    Last updated: {{ formatDate(item.updatedAt) }}
                    <span v-if="item.updatedBy"> by {{ item.updatedBy }}</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <div class="rollout-form__save mb-2">
              <v-btn
                color="primary"
                large
                class="mr-2"
                :loading="saving"
                data-testid="rollout-form-save"
                @click="save"
              >
                {{ isNew ? 'Create' : 'Save' }}
              </v-btn>
              <v-btn
                outlined
                large
                class="mr-2"
                :to="'/game-rollouts'"
                data-testid="rollout-form-cancel"
              >
                Cancel
              </v-btn>
              <v-btn
                v-if="!isNew && rolloutId"
                outlined
                large
                color="secondary"
                :to="changeLogLink"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="rollout-form-change-history"
              >
                <v-icon left>mdi-history</v-icon>
                History
              </v-btn>
            </div>
          </v-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ROLLOUT_STATUSES,
  ROLLOUT_SEGMENTATIONS,
  STATUS_LABELS,
  SEGMENTATION_LABELS,
  STATUS_DESCRIPTIONS,
  CHANGE_LOG_TABLE,
  formatRolloutDate,
} from './constants';
import { apiErrorMessage } from '../../../constants/analyticsConfigTypes';

export default {
  name: 'GameRolloutForm',
  props: {
    isNew: {
      type: Boolean,
      default: false,
    },
    rolloutId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      valid: false,
      error: '',
      item: {},
      feeds: [],
      games: [],
      form: {
        feedId: null,
        gameId: null,
        percentage: 0,
        segmentation: 'all',
        status: 'draft',
        comment: '',
      },
      rules: {
        required: (v) => !!(v != null && String(v).trim()) || 'Required.',
      },
    };
  },
  computed: {
    statusItems() {
      return ROLLOUT_STATUSES.map((value) => ({
        value,
        text: STATUS_LABELS[value] || value,
      }));
    },
    segmentationItems() {
      return ROLLOUT_SEGMENTATIONS.map((value) => ({
        value,
        text: SEGMENTATION_LABELS[value] || value,
      }));
    },
    statusDescription() {
      return STATUS_DESCRIPTIONS[this.form.status] || '';
    },
    percentageDisabled() {
      return this.form.status === 'stopped';
    },
    percentageMin() {
      if (this.form.status === 'active') return 5;
      return 0;
    },
    percentageMax() {
      return 100;
    },
    changeLogLink() {
      return {
        path: '/game-data-change-log',
        query: {
          table: CHANGE_LOG_TABLE,
          entityId: this.rolloutId != null ? String(this.rolloutId) : undefined,
        },
      };
    },
  },
  watch: {
    rolloutId: {
      immediate: true,
      handler(id) {
        if (!this.isNew && id) {
          this.loadRollout();
        }
      },
    },
  },
  async created() {
    if (this.isNew) {
      await this.loadLookups();
    }
  },
  methods: {
    formatDate: formatRolloutDate,
    gameLabel(game) {
      if (!game) return '';
      return game.slug ? `${game.name} (${game.slug})` : game.name;
    },
    formatFeedLabel() {
      const feed = this.item.feedGame?.feed;
      if (feed?.name) return feed.name;
      const id = this.form.feedId;
      return id == null ? '—' : `Feed #${id}`;
    },
    formatGameLabel() {
      const game = this.item.feedGame?.game;
      if (game?.name) {
        return game.slug ? `${game.name} (${game.slug})` : game.name;
      }
      const id = this.form.gameId;
      return id == null ? '—' : `Game #${id}`;
    },
    onStatusChange() {
      this.form.percentage = this.clampPercentage(this.form.percentage);
    },
    onPercentageSliderChange(value) {
      this.form.percentage = this.clampPercentage(value);
    },
    onPercentageInputChange() {
      this.form.percentage = this.clampPercentage(this.form.percentage);
    },
    clampPercentage(value) {
      let num = Number.isFinite(value) ? Math.round(value) : 0;
      if (this.form.status === 'stopped') return 0;
      if (this.form.status === 'active') {
        if (num < 1) num = 1;
        if (num > 100) num = 100;
        num = Math.round(num / 5) * 5 || 5;
        return num;
      }
      if (num < 0) num = 0;
      if (num > 100) num = 100;
      return num;
    },
    validateForm() {
      if (this.isNew) {
        if (this.form.feedId == null || this.form.feedId === '') {
          return 'Feed is required.';
        }
        if (this.form.gameId == null || this.form.gameId === '') {
          return 'Game is required.';
        }
      }
      if (this.form.status === 'active') {
        const pct = Number(this.form.percentage);
        if (!pct || pct < 1 || pct > 100) {
          return 'Active rollout requires percentage between 1 and 100.';
        }
      }
      if (this.form.status === 'stopped' && Number(this.form.percentage) !== 0) {
        return 'Stopped rollout requires percentage 0.';
      }
      return '';
    },
    applyItemToForm(item) {
      this.item = item || {};
      this.form = {
        feedId: item.feedGame?.feedId ?? item.feedGame?.feed?.id ?? null,
        gameId: item.feedGame?.gameId ?? item.feedGame?.game?.id ?? null,
        percentage: item.percentage ?? 0,
        segmentation: item.segmentation || 'all',
        status: item.status || 'draft',
        comment: item.comment || '',
      };
    },
    buildPayload() {
      const payload = {
        percentage: this.clampPercentage(this.form.percentage),
        segmentation: this.form.segmentation,
        status: this.form.status,
      };
      if (this.form.comment) {
        payload.comment = this.form.comment;
      }
      return payload;
    },
    async loadLookups() {
      try {
        await this.$msal.ensureAuthenticated();
        const [feeds, games] = await Promise.all([
          this.$gameApi.get('/feeds'),
          this.$gameApi.get('/games'),
        ]);
        this.feeds = Array.isArray(feeds) ? feeds : [];
        this.games = Array.isArray(games) ? games : [];
      } catch (error) {
        console.error('Error loading feeds/games:', error);
        this.error = 'Failed to load feeds and games.';
      }
    },
    async loadRollout() {
      this.loading = true;
      this.error = '';
      try {
        await this.$msal.ensureAuthenticated();
        const item = await this.$gameApi.get(`/feed-game-rollout/${this.rolloutId}`);
        this.applyItemToForm(item);
      } catch (error) {
        console.error('Error loading rollout:', error);
        this.error = apiErrorMessage(error, 'Failed to load rollout.');
      } finally {
        this.loading = false;
      }
    },
    async save() {
      if (this.$refs.form && !this.$refs.form.validate()) {
        return;
      }

      const validationError = this.validateForm();
      if (validationError) {
        this.error = validationError;
        return;
      }

      this.error = '';
      this.saving = true;
      try {
        await this.$msal.ensureAuthenticated();
        if (this.isNew) {
          const created = await this.$gameApi.post('/feed-game-rollout', {
            feedId: this.form.feedId,
            gameId: this.form.gameId,
            percentage: this.clampPercentage(this.form.percentage),
            segmentation: this.form.segmentation,
            status: this.form.status,
            ...(this.form.comment ? { comment: this.form.comment } : {}),
          });
          await this.$swal.fire({
            icon: 'success',
            title: 'Rollout created.',
            timer: 1500,
            showConfirmButton: false,
          });
          const id = created?.feedGameId ?? created?.feedGame?.id ?? created?.id;
          this.$emit('saved', id);
        } else {
          await this.$gameApi.patch(`/feed-game-rollout/${this.rolloutId}`, this.buildPayload());
          await this.$swal.fire({
            icon: 'success',
            title: 'Rollout updated.',
            timer: 1500,
            showConfirmButton: false,
          });
          await this.loadRollout();
          this.$emit('saved', this.rolloutId);
        }
      } catch (error) {
        if (error?.statusCode === 409 || error?.ErrorCode === 409) {
          this.error = 'A FeedGame for this feed and game already exists.';
        } else {
          this.error = apiErrorMessage(error, 'Failed to save rollout.');
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.rollout-form {
  width: 100%;
}

.rollout-form__tabs-shell {
  border: 1px solid rgba(0, 0, 0, 0.22);
  border-radius: 8px;
  /* overflow:hidden breaks sticky Save — same as game/feed shells */
  overflow: visible;
  background: #fff;
  margin-bottom: 16px;
}

.rollout-form__tab-panel {
  margin: 0;
  border: none;
  border-radius: 8px;
  background: #fff;
  min-height: 200px;
  box-shadow: none;
}

.rollout-form__tab-panel-body {
  padding: 0;
  overflow: visible;
}

.rollout-form__tab-panel-body > .mt-2 {
  margin-top: 0 !important;
  padding: 16px;
}

.rollout-form__tab-panel-body ::v-deep > .v-card {
  margin: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  border: none !important;
}

.rollout-form__section {
  border-left: 4px solid #1976d2;
  overflow: visible;
}

.rollout-form__section-title {
  display: flex;
  align-items: center;
  padding-top: 16px !important;
  padding-bottom: 12px !important;
  font-size: 1.15rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.01em;
}

.rollout-form__section ::v-deep > .v-card-text {
  padding-top: 4px !important;
  padding-bottom: 12px !important;
}

/* Space for floating labels when hide-details removes the default messages gap — same as Games */
.rollout-form__section ::v-deep .row--dense > .col,
.rollout-form__section ::v-deep .row--dense > [class*="col-"] {
  padding-top: 12px !important;
  padding-bottom: 12px !important;
}

/* Extra top room for always-visible slider thumb label */
.rollout-form__slider-col {
  padding-top: 28px !important;
}

.rollout-form__save {
  display: flex;
  justify-content: flex-start;
  position: sticky;
  bottom: 12px;
  z-index: 2;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(25, 118, 210, 0.2);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(6px);
}
</style>
